import React, { useEffect, useRef, useState, useCallback } from 'react';
import ChatBubble from '../components/common/ChatBubble';
import ChatInput from '../components/common/ChatInput';
import ScrollToTop from '../components/common/ScrollToTop';
import Header from '../components/Header';
import { sendMessageToAgent } from '../services/agentChatService';
import { extractCVText, validateCVFile } from '../services/cvParser';
import WizardSubmissionService from '../services/wizardSubmissionService';

const STORAGE_KEY = 'chat_n8n_history';
const SESSION_KEY = 'chat_n8n_session';

const INITIAL_MESSAGE = {
  id: 'initial',
  role: 'assistant',
  content:
    'Hello! 👋 I\'m your **Flight Plan™ Assistant**, powered by AI. I can help you with:\n\n- 🌍 Migration pathway recommendations\n- 📄 CV analysis and profile assessment\n- 💰 Cost estimates and requirements\n- 📋 Document checklists\n\nHow can I assist you today?',
};

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const extractReplyText = (raw) => {
  if (!raw) {
    return 'The automation returned an empty response.';
  }

  const tryParseString = (value) => {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    }
    return null;
  };

  const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

  const formatLabel = (key) =>
    key
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
      .join(' ');

  const formatPrimitive = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  const describeObjectForList = (value) => {
    if (!isPlainObject(value)) {
      return formatPrimitive(value);
    }

    return Object.entries(value)
      .map(([key, val]) => {
        const primitive = Array.isArray(val)
          ? val
            .map((item) => (isPlainObject(item) ? describeObjectForList(item) : formatPrimitive(item)))
            .filter(Boolean)
            .join(', ')
          : isPlainObject(val)
            ? describeObjectForList(val)
            : formatPrimitive(val);

        return primitive ? `${formatLabel(key)}: ${primitive}` : '';
      })
      .filter(Boolean)
      .join(' · ');
  };

  const formatArray = (values, indentLevel) => {
    const indent = '  '.repeat(indentLevel);
    return values
      .map((item) => {
        if (item === null || item === undefined) {
          return '';
        }
        if (Array.isArray(item)) {
          const nested = formatArray(item, indentLevel + 1);
          return nested ? `${indent}- ${nested}` : '';
        }
        if (isPlainObject(item)) {
          const summary = describeObjectForList(item);
          return summary ? `${indent}- ${summary}` : '';
        }
        const primitive = formatPrimitive(item);
        return primitive ? `${indent}- ${primitive}` : '';
      })
      .filter(Boolean)
      .join('\n');
  };

  const formatObject = (value, indentLevel = 0) => {
    const indent = '  '.repeat(indentLevel);
    const lines = Object.entries(value)
      .map(([key, val]) => {
        if (val === null || val === undefined) {
          return '';
        }

        if (Array.isArray(val)) {
          const formattedArray = formatArray(val, indentLevel + 1);
          return formattedArray ? `${indent}${formatLabel(key)}:\n${formattedArray}` : '';
        }

        if (isPlainObject(val)) {
          const nested = formatObject(val, indentLevel + 1);
          return nested ? `${indent}${formatLabel(key)}:\n${nested}` : '';
        }

        const primitive = formatPrimitive(val);
        return primitive ? `${indent}${formatLabel(key)}: ${primitive}` : '';
      })
      .filter(Boolean);

    return lines.join('\n');
  };

  const dig = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value !== 'object') return String(value);

    if (value.response || value.reply || value.text || value.message || value.output) {
      return (
        value.response ||
        value.reply ||
        value.text ||
        value.message ||
        value.output
      );
    }

    if (isPlainObject(value)) {
      const formatted = formatObject(value);
      if (formatted) {
        return formatted;
      }
    }

    return JSON.stringify(value, null, 2);
  };

  if (typeof raw === 'string') {
    const maybeJson = tryParseString(raw);
    if (maybeJson) {
      return extractReplyText(maybeJson);
    }
    return raw.trim();
  }

  return dig(raw);
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  const units = ['bytes', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const ChatN8nPage = () => {
  // Load persisted messages from localStorage
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to load chat history:', e);
    }
    return [INITIAL_MESSAGE];
  });

  const [isThinking, setIsThinking] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    return saved || null;
  });
  const scrollRef = useRef(null);

  // Handle mobile viewport changes when keyboard opens/closes
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    const setVisualViewportVars = () => {
      const vv = window.visualViewport;
      if (vv) {
        const vh = vv.height * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        const keyboardOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        document.documentElement.style.setProperty('--keyboard-offset', `${keyboardOffset}px`);
      }
    };

    setVH();
    setVisualViewportVars();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setVisualViewportVars);
      window.visualViewport.addEventListener('scroll', setVisualViewportVars);
    }

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setVisualViewportVars);
        window.visualViewport.removeEventListener('scroll', setVisualViewportVars);
      }
    };
  }, []);

  useEffect(() => {
    // Auto scroll to bottom of chat container on new messages
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Save messages to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      console.warn('Failed to save chat history:', e);
    }
  }, [messages]);

  // Save session ID to localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem(SESSION_KEY, sessionId);
    }
  }, [sessionId]);

  // Clear chat function
  const clearChat = useCallback(() => {
    setMessages([INITIAL_MESSAGE]);
    setSessionId(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_KEY);
  }, []);

  // Scroll progress indicator effect bound to chat messages scroll container
  useEffect(() => {
    const handleScroll = () => {
      const scrollProgress = document.getElementById('scroll-progress');
      const container = scrollRef.current;
      if (scrollProgress && container) {
        const maxScroll = Math.max(1, container.scrollHeight - container.clientHeight);
        const progress = (container.scrollTop / maxScroll) * 100;
        const clamped = Math.min(Math.max(progress, 0), 100);
        scrollProgress.style.width = `${clamped}%`;
        scrollProgress.setAttribute('aria-valuenow', clamped.toFixed(0));
      }
    };

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          entry.target.style.opacity = '1';
        }
      });
    }, observerOptions);

    // Observe elements
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    handleScroll(); // Initial check

    return () => {
      const container = scrollRef.current;
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const addMessage = (msg) => {
    setMessages((prev) => [...prev, msg]);
  };

  const forwardToWorkflow = async (messagePayload, metadata = {}) => {
    let effectiveSessionId = sessionId;
    if (!effectiveSessionId) {
      effectiveSessionId = createSessionId();
      setSessionId(effectiveSessionId);
    }

    const { reply, suggestions, sessionId: responseSessionId } = await sendMessageToAgent({
      message: messagePayload,
      sessionId: effectiveSessionId,
      extraParams: metadata,
    });

    if (responseSessionId && responseSessionId !== effectiveSessionId) {
      setSessionId(responseSessionId);
    }

    const content = extractReplyText(reply);

    addMessage({
      id: `a-${Date.now()}`,
      role: 'assistant',
      content: content || 'The automation did not provide any content.',
      suggestions: Array.isArray(suggestions) ? suggestions : undefined,
    });
  };

  const handleSuggestionClick = async (suggestion) => {
    const value = typeof suggestion === 'string' ? suggestion : suggestion.value || suggestion.label;
    if (value) {
      await handleUserInput(value);
    }
  };

  // Handle feedback on assistant messages
  const handleFeedback = useCallback((messageId, feedbackType) => {
    console.log('Feedback:', messageId, feedbackType);
    // Update message with feedback
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, feedback: feedbackType } : msg
    ));
    // TODO: Could send feedback to analytics/backend here
  }, []);

  // Handle retry for failed messages
  const handleRetry = useCallback(async (errorMessage) => {
    // Find the user message that preceded this error
    const msgIndex = messages.findIndex(m => m.id === errorMessage.id);
    if (msgIndex > 0) {
      const userMessage = messages[msgIndex - 1];
      if (userMessage && userMessage.role === 'user') {
        // Remove the error message
        setMessages(prev => prev.filter(m => m.id !== errorMessage.id));
        // Retry the user's message
        setIsThinking(true);
        try {
          await forwardToWorkflow(userMessage.content);
        } catch (err) {
          addMessage({
            id: `e-${Date.now()}`,
            role: 'assistant',
            content: `⚠️ ${err?.message || 'Retry failed. Please try again later.'}`,
          });
        } finally {
          setIsThinking(false);
        }
      }
    }
  }, [messages, forwardToWorkflow]);

  const handleUserInput = async (text) => {
    if (!text || !text.trim()) {
      return;
    }

    const trimmed = text.trim();
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
    addMessage(userMsg);
    setIsThinking(true);

    try {
      await forwardToWorkflow(trimmed);
    } catch (err) {
      const errorText = err?.message || 'Something went wrong while contacting the assistant.';

      addMessage({
        id: `e-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ ${errorText}`,
      });
    } finally {
      setIsThinking(false);
    }
  };

  const handleFileUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    addMessage({
      id: `upload-${Date.now()}`,
      role: 'assistant',
      content: `⏳ Reading ${file.name} (${formatFileSize(file.size)})…`,
    });

    setIsThinking(true);

    try {
      validateCVFile(file, 10); // Reuse existing validation (max 10MB)
      const cvText = await extractCVText(file);

      if (!cvText || cvText.trim().length < 50) {
        throw new Error('Could not extract enough text from the document. Please ensure the file has readable content.');
      }

      const cleanText = cvText.trim();
      const maxChars = 6000;
      const truncated = cleanText.length > maxChars ? `${cleanText.slice(0, maxChars)}\n\n[Text truncated after ${maxChars} characters]` : cleanText;

      addMessage({
        id: `summary-${Date.now()}`,
        role: 'assistant',
        content: `📄 Parsed ${file.name}. Sending the extracted text to our automation...\n\nPreview:\n${truncated.slice(0, 500)}${truncated.length > 500 ? '…' : ''}`,
      });

      const uploadInfo = await WizardSubmissionService.uploadCV(file, 'unknown');
      const cvPublicUrl = uploadInfo?.publicUrl || '';
      const cvPath = uploadInfo?.path || '';
      const cvSummary = truncated.slice(0, 500);
      const payload = `I am uploading my CV (${file.name}, ${formatFileSize(file.size)}).\nHere is the extracted text:\n\n${truncated}`;

      await forwardToWorkflow(payload, {
        filename: file.name,
        filesize: file.size.toString(),
        filetype: file.type || 'application/octet-stream',
        cv_url: cvPublicUrl,
        cv_public_url: cvPublicUrl,
        cv_storage_path: cvPath,
        cv_filename: uploadInfo?.fileName || file.name,
        cv_filesize: (uploadInfo?.fileSize ?? file.size).toString(),
        cv_summary: cvSummary,
      });
    } catch (err) {
      const errorText = err?.message || 'Failed to process the document. Please try again.';
      addMessage({
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ ${errorText}`,
      });
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <div
      className="flex flex-col bg-gradient-to-b from-slate-50 to-white"
      style={{
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
      }}
    >
      {/* Global Header with full navigation */}
      <Header />

      {/* Chat Sub-Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-100 px-4 sm:px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Flight Plan™ Assistant</h1>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                AI-Powered Migration Support
              </p>
            </div>
          </div>
          {messages.length > 1 && (
            <button
              onClick={clearChat}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-red-50"
              title="Clear conversation"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </div>
      </div>

      {/* Chat Content - Flex container that takes remaining height */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full max-w-5xl mx-auto">
        {/* Chat Messages - Scrollable area */}
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          <div className="space-y-3 sm:space-y-4">
            {messages.map((message) => (
              <ChatBubble
                key={message.id}
                message={message}
                onSuggestionClick={handleSuggestionClick}
                onFeedback={handleFeedback}
                onRetry={handleRetry}
              />
            ))}
            {isThinking && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="bg-white border border-blue-100 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chat Input - Pinned at bottom */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-3">
          <ChatInput onSend={handleUserInput} isThinking={isThinking} onFileUpload={handleFileUpload} />
        </div>
      </div>

      {/* Scroll to Top Button - Hidden on mobile to avoid interference */}
      <div className="hidden sm:block">
        <ScrollToTop autoScroll={false} showAfter={300} />
      </div>
    </div>
  );
};

export default ChatN8nPage;
